import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Modal from '@oberoninternal/travelbase-ds/components/layout/Modal';
import ModalHeader from '@oberoninternal/travelbase-ds/components/layout/ModalHeader';
import { getAccommodationTypeOptions } from '../utils/getAccommodationTypeOptions';
import Formed from './Formed';
import { SearchBarValues } from './SearchBox';
import { useTenantContext } from '../context/TenantContext';
import SearchFormMobile from './SearchFormMobile';
import useFullscreenToggleEvent from '../hooks/useFullscreenToggleEvent';
import { Interactable } from './SearchForm';

interface Props {
    open: boolean;
    onClose: () => void;
    submitForm: () => void;
    setValues: (values: SearchBarValues, shouldValidate?: boolean) => void;
    values: SearchBarValues;
    active?: Interactable[];
}

const SearchBoxModal: FC<React.PropsWithChildren<Props>> = ({
    open,
    onClose,
    setValues,
    values,
    submitForm,
    active,
}) => {
    const intl = useIntl();
    const { brandConfig } = useTenantContext();
    const accommodationTypeOptions = getAccommodationTypeOptions(intl, brandConfig);
    useFullscreenToggleEvent(open);

    return (
        <StyledModal open={open} onClose={onClose} variant="regular">
            <Formed
                handleSubmit={innerValues => {
                    setValues({ ...values, ...innerValues });
                    return submitForm();
                }}
                initialValues={values}
                skipPrompt
            >
                {({ submitForm: submitInner, isSubmitting }) => (
                    <>
                        <StickyHeader>
                            <ModalHeader style={{ textTransform: 'capitalize' }}>
                                <FormattedMessage defaultMessage="Zoeken" />
                            </ModalHeader>
                        </StickyHeader>
                        <Body>
                            <SearchFormMobile active={active} accommodationTypeOptions={accommodationTypeOptions} />
                        </Body>
                        <StickyFooter>
                            <SearchButton
                                size="large"
                                onClick={async () => {
                                    await submitInner();
                                    onClose();
                                }}
                                data-cy="searchButton"
                                submitting={isSubmitting}
                            >
                                <FormattedMessage defaultMessage="Zoeken" />
                            </SearchButton>
                        </StickyFooter>
                    </>
                )}
            </Formed>
        </StyledModal>
    );
};

export default SearchBoxModal;

const sidePadding = css`
    padding-left: 2rem;
    padding-right: 2rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-left: 3.2rem;
        padding-right: 3.2rem;
    }
`;

const StickyHeader = styled.div`
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${({ theme }) => theme.colors.neutral['0']};
    ${sidePadding};
    padding-top: 2rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-top: 3.2rem;
    }
`;

const Body = styled.div`
    ${sidePadding};
    flex: 1;
    padding-top: ${({ theme }) => theme.spacing['50_Semi']};
    padding-bottom: ${({ theme }) => theme.spacing['50_Semi']};
`;

const StickyFooter = styled.div`
    position: sticky;
    bottom: 0;
    z-index: ${({ theme }) => theme.zIndices.banner};
    background: ${({ theme }) => theme.colors.neutral['0']};
    ${sidePadding};
    padding-top: ${({ theme }) => theme.spacing['50_Semi']};
    padding-bottom: 2rem;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        padding-bottom: 3.2rem;
    }
`;

const SearchButton = styled(Button)`
    width: 100%;
    border-radius: ${({ theme }) => theme.radius.button};
`;

const StyledModal = styled(Modal)`
    && {
        padding: 0;
    }
    && > form {
        display: flex;
        flex-direction: column;
        min-height: 100%;
    }
`;
